import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { getVideoBySlug, VIDEOS } from "@/lib/learningHub";
import { VIDEO_TRANSCRIPTS } from "@/lib/videoTranscripts";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { ChevronLeft, ChevronDown, FileText, PlayCircle } from "lucide-react";
import { useState } from "react";

const SITE_URL = "https://coloradowebimpressions.com";

export const Route = createFileRoute("/learn/videos/$videoSlug")({
  loader: ({ params }) => {
    const video = getVideoBySlug(params.videoSlug);
    if (!video) throw notFound();
    return { video };
  },
  head: ({ loaderData }) => {
    const v = loaderData?.video;
    if (!v) return { meta: [{ title: "Video not found" }] };
    const url = `${SITE_URL}/learn/videos/${v.slug}`;
    const ogImage = `https://i.ytimg.com/vi/${v.youtubeId}/maxresdefault.jpg`;
    return {
      meta: [
        { title: `${v.title} | Colorado Web Impressions` },
        { name: "description", content: v.desc },
        { property: "og:title", content: v.title },
        { property: "og:description", content: v.desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "video.other" },
        { property: "og:image", content: ogImage },
        { property: "og:video", content: `https://www.youtube.com/watch?v=${v.youtubeId}` },
        ...twitterTextTags({ title: v.title, description: v.desc }),
        { name: "twitter:card", content: "player" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: v.title,
            description: v.desc,
            thumbnailUrl: [ogImage],
            uploadDate: v.datePublished ?? "2025-01-01",
            embedUrl: `https://www.youtube.com/embed/${v.youtubeId}`,
            contentUrl: `https://www.youtube.com/watch?v=${v.youtubeId}`,
            ...(VIDEO_TRANSCRIPTS[v.slug]
              ? { transcript: VIDEO_TRANSCRIPTS[v.slug] }
              : {}),
          }),
        },
        buildBreadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Learn", path: "/learn" },
          { name: "Videos", path: "/learn" },
          { name: v.title, path: `/learn/videos/${v.slug}` },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-3xl font-extrabold">Video not found</h1>
        <p className="mt-3 text-muted-foreground">That video doesn't exist.</p>
        <Link to="/learn" className="mt-6 inline-block font-bold text-brand-gold-deep underline">
          Back to Learning Center
        </Link>
      </div>
    </SiteLayout>
  ),
  errorComponent: () => (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-3xl font-extrabold">Something went wrong</h1>
        <p className="mt-3 text-muted-foreground">We couldn't load this video — please try again.</p>
      </div>
    </SiteLayout>
  ),
  component: VideoPage,
});

function VideoPage() {
  const { video } = Route.useLoaderData();
  const related = VIDEOS.filter((v) => v.slug !== video.slug).slice(0, 3);
  const transcript = VIDEO_TRANSCRIPTS[video.slug];
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  return (
    <SiteLayout>
      <PageHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Learning Center", to: "/learn" },
          { label: video.title },
        ]}
        eyebrow={`Video • ${video.category}`}
        title={video.title}
        description={video.desc}
      />

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="relative aspect-video w-full bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0`}
                title={video.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>

          <div className="mt-8 prose prose-lg max-w-none text-foreground/90">
            <p className="text-lg leading-relaxed">{video.desc}</p>
            <p className="text-sm text-muted-foreground">
              Recorded by Chris Heidlebaugh, owner of Colorado Web Impressions — 25 years building
              websites and SEO for Colorado small businesses, in-house, no outsourcing.
            </p>
          </div>


          {transcript && (
            <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <button
                type="button"
                onClick={() => setTranscriptOpen((o) => !o)}
                aria-expanded={transcriptOpen}
                aria-controls="video-transcript"
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-smooth hover:bg-muted/40 sm:px-6"
              >
                <span className="flex items-center gap-2.5">
                  <FileText className="h-5 w-5 text-brand-gold-deep" />
                  <span className="text-base font-extrabold text-foreground sm:text-lg">
                    {transcriptOpen ? "Hide transcript" : "Show transcript"}
                  </span>
                </span>
                <ChevronDown
                  className={`h-5 w-5 text-muted-foreground transition-transform ${
                    transcriptOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {transcriptOpen && (
                <div
                  id="video-transcript"
                  className="border-t border-border bg-background/60 px-5 py-6 sm:px-6"
                >
                  <p className="mb-4 text-xs uppercase tracking-wide text-muted-foreground">
                    AI-assisted summary of what Chris covers in this video. Watch above for the full
                    conversation.
                  </p>
                  <div className="space-y-4 text-base leading-relaxed text-foreground/90">
                    {transcript
                      .split(/\n\s*\n/)
                      .map((para, i) => (
                        <p key={i}>{para.trim()}</p>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/learn"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep transition-smooth hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" /> Back to Learning Center
            </Link>
            {related.length > 0 && (
              <a
                href="#watch-next"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("watch-next")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold-deep px-5 py-2.5 text-sm font-bold text-background shadow-card transition-spring hover:-translate-y-0.5 hover:shadow-card-hover"
              >
                <PlayCircle className="h-4 w-4" /> Watch next
              </a>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section id="watch-next" className="bg-section-alt py-12 sm:py-16 scroll-mt-24">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground">More videos</h2>
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((v) => (
                <li key={v.slug}>
                  <Link
                    to="/learn/videos/$videoSlug"
                    params={{ videoSlug: v.slug }}
                    className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
                  >
                    <div className="relative aspect-video bg-black">
                      <img
                        src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                        alt={v.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-extrabold text-foreground group-hover:text-brand-gold-deep">
                        {v.title}
                      </h3>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <FinalCTA />
    </SiteLayout>
  );
}
